import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import TextField from '../../components/formfields/TextField';
import RichSelectField from '../../components/formfields/RichSelectField';
import PhoneNumberField from '../../components/formfields/PhoneNumberField';
import { 
  countryOptions, 
  getStateProvinceOptions,
  getPostalCodeInfo,
  hasStructuredStates 
} from '../../constants/address';
import { useScrollTo } from '../../hooks/useScrollTo';
import { useFormStore } from '../../stores/formData';

const extractValue = (data) => {
  // Check if it's a SyntheticBaseEvent
  if (data && typeof data === 'object' && data.target && data.target.value !== undefined) {
    return data.target.value;
  }
  
  // Check if it's a regular object with a value property
  if (data && typeof data === 'object' && data.value !== undefined) {
    return data.value;
  }
  
  // Return as-is if it's a primitive value
  return data;
};

const prefixField = (prefix, field) => {
  return prefix ? `${prefix}_${field}` : field;
};

const AddressForm = ({
  legIndex,
  addressType = 'origin', // 'origin' or 'destination'
  fieldPrefix = '',
  initialAddress = {},
  onAddressChange,
  className = ''
}) => {
  const { theme } = useTheme();
  const [availableShips, setAvailableShips] = useState([]);
  const [availableItineraries, setAvailableItineraries] = useState([]);
  const [hasPrefilledCustomerInfo, setHasPrefilledCustomerInfo] = useState(false);
  const { scrollToTop } = useScrollTo();
  const isDark = theme === 'dark';
  const isMountedRef = useRef(false);

  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const customerInfo = useFormStore((state) => state.formData.customerInfo);
  const onBehalfUser = useFormStore((state) => state.formData.onBehalfUser);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

   // State for address fields
  const [address, setAddress] = useState(initialAddress);
   
  // Get appropriate state/province options based on country
  const stateProvinceInfo = useMemo(() => {
    return getStateProvinceOptions(address.country_code);
  }, [address.country_code]);

  // Get postal code label and placeholder based on country
  const postalCodeInfo = useMemo(() => {
    return getPostalCodeInfo(address.country_code);
  }, [address.country_code]);

  // Check if country uses structured state/province system
  const countryHasStructuredStates = useMemo(() => {
    return hasStructuredStates(address.country_code);
  }, [address.country_code]);


  // Handle field changes with country-specific logic
  const handleFieldChange = useCallback((field, value) => {
    if (!isMountedRef.current) return;

    const extractedValue = extractValue(value);

    setAddress(prev => {
      const updated = { ...prev, [field]: extractedValue };

      // Clear state/province when country changes
      if (field === 'country_code' && extractedValue !== prev.country_code) {
        // get the country name
        const countryName = countryOptions.find(c => c.value === extractedValue)?.label || '';
        updated.country = countryName;
        updated.state = '';
      }

      // Update zip and postal code to same values
      if (field === 'zip' && extractedValue !== prev.zip) {
        updated.zip = extractedValue;
        updated.zip5 = extractedValue;
        updated.postal_code = extractedValue;
      }

      // Clear ship name when cruise line changes
      if (field === 'cruiseLine' && extractedValue !== prev.cruiseLine) {
        updated.shipName = '';
      }
      
      // Call the callback with updated address
      // onAddressChange(updated);
      return updated;
    });
  }, [onAddressChange]);

  const handleSaveField = useCallback((field, value) => {
    // Save the data when the field loses focus
    const extractedValue = extractValue(value);
    setAddress(prev => {
      const updated = {
        ...prev,
        [field]: extractedValue
      };
      onAddressChange?.(updated); // Use the updated value, not the stale state
      return updated;
    });
  }, [onAddressChange]);

  useEffect(() => {
    if(address.cruisePort && address.cruiseFilter) {
      const cruiseLine = address?.cruiseLine;
      if (!cruiseLine) return null;

      const cruiseLineData = helpers.getCruiseData()?.find(line => line.id === cruiseLine.id);
      if (!cruiseLineData) return null;

      const port = address.cruisePort;
      const matchedItineraries = cruiseLineData.cruise_sessions?.filter(itinerary => {
        const matchEmbarkPort =  (addressType === 'origin' && itinerary.embarkation_port_id === port.port_id);
        const matchDisembarkPort =  (addressType === 'destination' && 
          itinerary.disembarkation_port_id === port.port_id);
        return (matchEmbarkPort || matchDisembarkPort);
      });

      const shipMap = {}
      matchedItineraries?.forEach(itinerary => {
        const shipName = itinerary[address.cruiseFilter];
        if(shipName) shipMap[shipName] = shipName;
      });
      setAvailableShips(Object.keys(shipMap));
    }
  },[helpers, address.cruisePort, address.cruiseFilter]);

  useEffect(() => {
    if(address.cruiseLine && (address.shipName || address.tourName)) {
      const cruiseLine = address.cruiseLine;
      const cruiseData = helpers.getCruiseLineData(cruiseLine?.id);
      if (!cruiseData) return null;
      const port = address.cruisePort;
      const matchedItineraries = cruiseData.cruise_sessions?.filter(itinerary => {
        const matchEmbarkPort =  (addressType === 'origin' && itinerary.disembarkation_port_id === port.port_id);
        const matchDisembarkPort =  (addressType === 'destination' && 
          itinerary.embarkation_port_id === port.port_id);
        const matchedShip = itinerary[address.cruiseFilter] == address.shipName ||
         itinerary[address.cruiseFilter] == address.tourName;
        return (matchedShip && (matchEmbarkPort || matchDisembarkPort));
      });
      setAvailableItineraries(matchedItineraries);
    }
  },[helpers, address.cruiseFilter, address.cruisePort, address.shipName, address.tourName]);

  const containerClassName = clsx(
    {
      'md:pr-6 md:border-r': addressType === 'origin',
      'md:pl-0': addressType === 'destination',
    },
    {
      'bg-slate-800 border-slate-700': isDark,
      'bg-white border-gray-200': !isDark
    },
    className
  );

  const displayTextClassName = clsx(
    'text-base',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const displayLabelClassName = clsx(
    'text-sm font-bold',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const gridClassName = 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-4';
  const fullWidthClassName = 'md:col-span-2';

  const isCruiseAddress = useMemo(() => {
    return (address.cruise == true);
  }, [address]);

  const cruiseLabels = useMemo(() => {
    return {
      placeholder: address.cruiseFilter == 'tour_name' ? 'Select tour name' : 'Select ship name',
      label: address.cruiseFilter == 'tour_name' ? 'Tour name' : 'Ship name',
      fieldName: address.cruiseFilter == 'ship_name' ? 'shipName' : 'tourName'
    };
  }, [address.cruiseFilter]);

  useEffect(() => {
    if ((onBehalfUser?.firstName || customerInfo?.firstName)) {
      // Use setTimeout to defer state updates until after render
      setTimeout(() => {
        if (addressType == 'origin') {
          actions.leg.updateField(legIndex, 'fromAddress', {
            ...address,
            senderName: `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`,
            email: onBehalfUser?.email || customerInfo?.email,
            phoneNumber: onBehalfUser?.phoneNumber || customerInfo?.phoneNumber,
            nameOnReservation: `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`,
          });
        }

        if (addressType == 'destination') {
          actions.leg.updateField(legIndex, 'toAddress', {
            ...address,
            recipientName: `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`,
            email: onBehalfUser?.email || customerInfo?.email,
            phoneNumber: onBehalfUser?.phoneNumber || customerInfo?.phoneNumber,
            nameOnReservation: `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`,
          });
        }

        const traveler = helpers.getTraveler()
        if (traveler?.email != (onBehalfUser?.email || customerInfo?.email)) {
          actions.form.updateField('traveler', {
            name: `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`,
            email: onBehalfUser?.email || customerInfo?.email,
            phone: onBehalfUser?.phoneNumber || customerInfo?.phoneNumber
          })
        }
        
        setHasPrefilledCustomerInfo(true);
      }, 0);
    }
  }, [onBehalfUser?.firstName, customerInfo?.firstName, isCruiseAddress, hasPrefilledCustomerInfo]);

  return (
    <div className={containerClassName}>
      {isCruiseAddress ? (
        // Cruise Address Form
        <div className="space-y-4">
          {/* Port, Cruise Line, Country - Display Only */}
          <div className="flex flex-col items-start">
            <div className="flex flex-row gap-2 items-center mb-2">
              <div className={displayLabelClassName}>Port:</div>
              <div className={displayTextClassName}>{address?.cruisePort?.name}</div>
            </div>
            <div className="flex flex-row gap-2 items-center mb-2">
              <div className={displayLabelClassName}>Cruise line:</div>
              <div className={displayTextClassName}>{address?.cruiseLine?.name}</div>
            </div>
            <div className="flex flex-row gap-2 items-center mb-2">
              <div className={displayLabelClassName}>Country:</div>
              <div className={displayTextClassName}>{address?.country}</div>
            </div>
          </div>

          {/* Cruise Line and Ship Name */}
          <div className={gridClassName}>
            <RichSelectField
              id={prefixField(fieldPrefix, cruiseLabels.fieldName)}
              label={cruiseLabels.label}
              value={address[cruiseLabels.fieldName] || ''}
              onChange={(value) => handleSaveField(cruiseLabels.fieldName, value)}
              placeholder={cruiseLabels.placeholder}
              options={availableShips}
              disabled={!address.cruiseLine}
              useCustomDropdown={true}
            />

            {/* Name on Reservation */}
            <TextField
              id={prefixField(fieldPrefix, 'nameOnReservation')}
              data-testid={prefixField(fieldPrefix, 'nameOnReservation')}
              label="Name on reservation"
              placeholder="Enter name"
              value={address.nameOnReservation || `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}` ||  ''}
              onChange={(value) => handleFieldChange('nameOnReservation', value)}
              onSaveOnBlur={() => handleSaveField('nameOnReservation', address.nameOnReservation)  }
            />
          </div>
          {availableItineraries && 
            <div className={gridClassName}>
              <RichSelectField
                id={prefixField(fieldPrefix, 'cruiseselectedItinerarySession')}
                label={'Itinerary'}
                value={address.selectedItinerary?.id || ''}
                onChange={(e) => {
                  const itinerary = availableItineraries.find(itin => itin.id === e.target.value);
                  handleSaveField('selectedItinerary', itinerary);
                }}
                placeholder={'Select your intinerary'}
                options={availableItineraries}
                valueKey={'id'}
                formatValue={(itinerary) => ({
                  id: itinerary.id,
                  key: itinerary.id,
                  label: `${itinerary.embarkation_date} - ${itinerary.disembarkation_date} - ${itinerary[address.cruiseFilter]} - ${itinerary.embarkation_port} to ${itinerary.disembarkation_port}`,
                })}
                containerClassName={fullWidthClassName}
                useCustomDropdown={true}
              />
            </div>
          }
          {/* Reservation Number and Stateroom */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'reservationNumber')}
              data-testid={prefixField(fieldPrefix, 'reservationNumber')}
              label="Reservation number"
              placeholder="Optional"
              value={address.reservationNumber || ''}
              onChange={(value) => handleFieldChange('reservationNumber', value)}
              onSaveOnBlur={() => handleSaveField('reservationNumber', address.reservationNumber)  }
            />
            <TextField
              id={prefixField(fieldPrefix, 'stateroom')}
              data-testid={prefixField(fieldPrefix, 'stateroom')}
              label="Stateroom"
              placeholder="Optional"
              value={address.stateroom || ''}
              onChange={(value) => handleFieldChange('stateroom', value)}
              onSaveOnBlur={() => handleSaveField('stateroom', address.stateroom)}
            />
          </div>
        </div>
      ) : (
        // Standard Address Form
        <div className="space-y-4">
          {/* Recipient Name and Phone */}
          <div className={gridClassName}>
            {addressType == 'origin' && 
              <TextField
                id={prefixField(fieldPrefix, 'senderName')}
                data-testid={prefixField(fieldPrefix, 'senderName')}
                label="Sender name"
                placeholder="Enter sender name"
                value={address.senderName || `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`}
                onChange={(value) => handleFieldChange('senderName', value)}
                required={true}
                onSaveOnBlur={() => handleSaveField('senderName', address.senderName)}
              />
            }
            {addressType == 'destination' && 
              <TextField
                id={prefixField(fieldPrefix, 'recipientName')}
                data-testid={prefixField(fieldPrefix, 'recipientName')}
                label="Recipient name"
                placeholder="Enter recipient name"
                value={address.recipientName || `${onBehalfUser?.firstName || customerInfo?.firstName} ${onBehalfUser?.lastName || customerInfo?.lastName}`}
                onChange={(value) => handleFieldChange('recipientName', value)}
                required={true}
                onSaveOnBlur={() => handleSaveField('recipientName', address.recipientName)}
              />
            }
            <PhoneNumberField
              data-testid={prefixField(fieldPrefix, 'phoneNumber')}
              label="Phone number"
              type="tel"
              placeholder="Enter phone number"
              value={address.phoneNumber || onBehalfUser?.phoneNumber || customerInfo?.phoneNumber || ''}
              onChange={(value) => handleFieldChange('phoneNumber', value)}
              onBlur={() => handleSaveField('phoneNumber', address.phoneNumber)}
            />
          </div>

          {/* Email and Company */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'email')}
              data-testid={prefixField(fieldPrefix, 'email')}
              label="Email"
              placeholder="Enter email"
              value={address.email || onBehalfUser?.email || customerInfo?.email || ''}
              onChange={(value) => handleFieldChange('email', value)}
              onSaveOnBlur={() => handleSaveField('email', address.email)}  
            />
            <TextField
              id={prefixField(fieldPrefix, 'companyName')}
              data-testid={prefixField(fieldPrefix, 'companyName')}
              label="Company Name (optional)"
              placeholder="Enter company name"
              value={address.companyName || ''}
              onChange={(value) => handleFieldChange('companyName', value)}
              onSaveOnBlur={() => handleSaveField('companyName', address.companyName)}
            />
          </div>

          {/* Address and Country */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'streetAddress')}
              data-testid={prefixField(fieldPrefix, 'streetAddress')}
              label="Street Address"
              placeholder="Enter street address"
              disabled={true}
              value={address.streetAddress || address.name || address.street || (address.formatted_address?.split?.[0] || '')}
              onChange={(value) => handleFieldChange('streetAddress', value)}
              onSaveOnBlur={() => handleSaveField('streetAddress', address.streetAddress)}
              className="bg-slate-300"
            />

            <TextField
              id={prefixField(fieldPrefix, 'address2')}
              data-testid={prefixField(fieldPrefix, 'address2')}
              label="Apt #, Suite etc. (optional)"
              placeholder=""
              value={address.address2 || ''}
              onChange={(value) => handleFieldChange('address2', value)}
              onSaveOnBlur={() => handleSaveField('address2', address.address2)}
            />
          </div>

          {/* City, State/Province, Postal Code */}
          {!isCruiseAddress && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                id={prefixField(fieldPrefix, 'city')}
                data-testid={prefixField(fieldPrefix, 'city')}
                label="City"
                placeholder="Enter city"
                value={address.city || ''}
                onChange={(value) => handleFieldChange('city', value)}
                required={false}
                disabled={true}
                onSaveOnBlur={() => handleSaveField('city', address.city)}
              />
              
              {countryHasStructuredStates ? (
                <RichSelectField
                  id={prefixField(fieldPrefix, 'state')}
                  label={stateProvinceInfo.label}
                  value={address.state || ''}
                  onChange={(value) => handleSaveField('state', value)}
                  options={stateProvinceInfo.options}
                  placeholder={stateProvinceInfo.placeholder}
                  required={false}
                  disabled={true}
                  useCustomDropdown={true}
                />
              ) : (
                <TextField
                  id={prefixField(fieldPrefix, 'state')}
                  data-testid={prefixField(fieldPrefix, 'state')}
                  label={stateProvinceInfo.label}
                  placeholder={stateProvinceInfo.placeholder}
                  value={address.state || ''}
                  required={false}
                  disabled={true}
                  onChange={(value) => handleFieldChange('state', value)}
                  onSaveOnBlur={() => handleSaveField('state', address.state)}
                />
              )}
              
              <TextField
                id={prefixField(fieldPrefix, 'zipCode')}
                data-testid={prefixField(fieldPrefix, 'zipCode')}
                label={postalCodeInfo.label}
                placeholder={postalCodeInfo.placeholder}
                value={address.zip || address.zip5 || address.postal_code || ''}
                onChange={(value) => handleFieldChange('zip', value)}
                regexMessage="Invalid postal code format"
                validateOnChange={true}
                validationType="postalCode"
                countryCode={address.country_code}
                required={false}
                disabled={true}
                //required={address.country_code === 'US' || address.country_code === 'CA'}
                onSaveOnBlur={() => handleSaveField('zipCode', address.zip)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

AddressForm.propTypes = {
  legIndex: PropTypes.number.isRequired,
  fieldPrefix: PropTypes.string,
  addressType: PropTypes.string,
  initialAddress: PropTypes.object,
  onAddressChange: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default AddressForm;